import React from 'react';
import '../../styles/caller-sentiment.css';

// Caller sentiment: current label, a word-sized trend sparkline, and a
// plain-language trend sentence (console v3 sentiment piece, 2026-09-21).
//
// BOUNDARY — presentational only. It renders the model built by
// lib/sentimentTrend.js#callerSentimentModel and nothing else: no data
// fetching, no context, no sentiment maths, and no assumptions about where it
// sits. It fills whatever width its parent gives it, sets no outer margins,
// and takes its colours from --cs-* custom properties (defaulting to the dark
// left-rail tokens), so a layout redesign can move or re-theme it without
// touching this file or re-deriving the series.
//
// SPARKLINE-SCALE, deliberately (Storm, 2026-09-21): at most MAX_BARS tiny
// bars, no axes, gridlines, scale, legend or numbers. Three coarse labels per
// utterance do not support more, and the agent is mid-call.
const LABEL_TEXT = { POSITIVE: 'Positive', NEUTRAL: 'Neutral', NEGATIVE: 'Negative' };
const BAR_W = 3;
const GAP = 1.5;
const H = 14;
const HALF = H / 2;
const MIN_BAR = 1.5;

export function SentimentSpark({ bars }) {
  if (!bars || !bars.length) return null;
  const width = bars.length * (BAR_W + GAP) - GAP;
  return (
    <svg className="cs-spark" width={width} height={H} viewBox={`0 0 ${width} ${H}`} aria-hidden="true" focusable="false">
      {bars.map((b, i) => {
        const h = Math.max(MIN_BAR, Math.abs(b.v) * (HALF - 0.5));
        const y = b.sign === 'positive' ? HALF - h : b.sign === 'negative' ? HALF : HALF - h / 2;
        return <rect key={i} className={`cs-bar ${b.sign}`} x={i * (BAR_W + GAP)} y={y} width={BAR_W} height={h} rx="0.75" />;
      })}
    </svg>
  );
}

export default function CallerSentiment({ model, className = '' }) {
  if (!model) return null;
  return (
    <div className={`cs ${className}`.trim()} role="group" aria-label={model.ariaLabel}>
      <div className="cs-line">
        <span className={`cs-label ${model.label.toLowerCase()}`}>{LABEL_TEXT[model.label] ?? model.label}</span>
        <SentimentSpark bars={model.bars} />
      </div>
      {model.sentence && <p className="cs-sentence">{model.sentence}</p>}
    </div>
  );
}
